import { Box, Checkbox, List, ListItemButton, ListItemIcon, ListItemText, Typography } from '@mui/material';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useRefinementList } from 'react-instantsearch';
import SearchInput from '../../common/components/SearchInput';
import useNormalizedCurrentRefinements from '../../hooks/useNormalizedCurrentRefinements';
import FilterUtils from './filter.utils';

interface AdministrativeAreasByLevel {
  [key: string]: AreaInfo[];
}

interface AreaInfo {
  value: string;
  label: string;
  isRefined: boolean;
}

export default function GeoLocationAdministrativeAreaFilter(): React.JSX.Element {
  const { items, refine, searchForItems } = useRefinementList({ limit: 1000, attribute: 'administrative_areas' });
  const currentItems = useNormalizedCurrentRefinements('administrative_areas') as string[];
  const [searchItemsValue, setSearchItemsValue] = useState('');
  const { t } = useTranslation(['discovery']);

  const handleSearchChange = (value: string): void => {
    setSearchItemsValue(value);
    searchForItems(value);
  };

  const administrativeAreasByLevel = useMemo((): AdministrativeAreasByLevel => {
    return items.reduce((acc, area) => {
      const areaData = FilterUtils.ExtractAdministrativeAreaInfo(area.value);
      const areaInfo: AreaInfo = {
        value: area.value,
        label: areaData[1],
        isRefined: area.isRefined || currentItems.includes(area.value),
      };
      const level = acc[areaData[0]];

      if (level) {
        level.push(areaInfo);
      } else {
        acc[areaData[0]] = [areaInfo];
      }

      return acc;
    }, {} as AdministrativeAreasByLevel);
  }, [items, currentItems]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
      <Box sx={{ px: 2, mb: 1 }}>
        <SearchInput
          fullWidth
          value={searchItemsValue}
          onChange={handleSearchChange}
          placeholder={t('discovery:administrative_area_placeholder')}
        />
      </Box>
      {Object.keys(administrativeAreasByLevel).sort((a, b) => a.localeCompare(b)).map((key) => (
        <Box key={key}>
          <Typography variant="bodySmall" sx={{ px: 2 }}>
            {t(`discovery:${key}`)}
          </Typography>
          <List>
            {administrativeAreasByLevel[key]
              .sort(({ label: labelA }, { label: labelB }) => labelA.toLowerCase().localeCompare(labelB.toLowerCase()))
              .map(({ value, label, isRefined }) => (
                <ListItemButton key={value} onClick={() => refine(value)}>
                  <ListItemText primary={label} />
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={isRefined}
                    />
                  </ListItemIcon>
                </ListItemButton>
              ))}
          </List>
        </Box>
      ))}
    </Box>
  );
}
